/**
 * Renders one custom section (Local Government Admin content) in the same
 * structure as ChartLayout so it appears in PDF/Word reports (chart-section, key-metrics, content-section).
 */

import React from 'react';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import type { CustomSection } from '../../services/customSectionsService';

interface CustomSectionBlockProps {
  section: CustomSection;
}

/** Sanitize for minimal safe HTML (strip script, etc.). For Phase 1 we allow limited tags. */
function sanitizeHtml(html: string): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  const allowedTags = ['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'A', 'UL', 'OL', 'LI', 'H4', 'H5'];
  const walk = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (!allowedTags.includes(el.tagName)) {
        const span = document.createElement('span');
        while (el.firstChild) span.appendChild(el.firstChild);
        if (el.parentNode) {
          el.parentNode.replaceChild(span, el);
        } else {
          el.appendChild(span);
        }
        let c = span.firstChild;
        while (c) {
          const next = c.nextSibling;
          walk(c);
          c = next;
        }
        return;
      }
      if (el.tagName === 'A') {
        const href = el.getAttribute('href') || '';
        if (!href.startsWith('http://') && !href.startsWith('https://') && !href.startsWith('/')) {
          el.removeAttribute('href');
        }
      }
    }
    let child = node.firstChild;
    while (child) {
      const next = child.nextSibling;
      walk(child);
      child = next;
    }
  };
  walk(div);
  return div.innerHTML;
}

export const CustomSectionBlock: React.FC<CustomSectionBlockProps> = ({ section }) => {
  const contentHtml = section.content_html ? sanitizeHtml(section.content_html) : '';

  return (
    <div className="chart-section custom-section-block" id={`custom-${section.section_slug}`}>
      <div className="chart-content">
        <div className="chart-title-section">
          <h3>{section.section_title}</h3>
          {section.metrics && section.metrics.length > 0 && (
            <div className="key-metrics-after-title">
              <KeyMetrics>
                {section.metrics.map((m) => (
                  <KeyMetric
                    key={m.id}
                    value={m.value}
                    label={m.label}
                    color={m.color_hex || undefined}
                  />
                ))}
              </KeyMetrics>
            </div>
          )}
        </div>
        <div className="chart-content-layout single-column">
          <div className="chart-left-side">
            {section.image_url && (
              <div className="chart-container custom-section-image">
                <img
                  src={section.image_url}
                  alt=""
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
            )}
          </div>
          <div className="chart-right-side">
            <div className="content-sections">
              <div className="content-section">
                {contentHtml && (
                  <div
                    className="custom-section-content"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                )}
                {section.link_url && (
                  <p style={{ marginTop: '1rem' }}>
                    <a href={section.link_url} target="_blank" rel="noopener noreferrer">
                      {section.link_label || section.link_url}
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSectionBlock;
